import React from 'react';
import { ThumbsUp, ThumbsDown, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Message } from '../../types/chat';

interface RegularMessageProps {
  message: Message;
  onFeedback: (messageId: string, feedback: 'thumbs_up' | 'thumbs_down') => void;
}

// Helper function to detect and format JSON content
const formatContent = (content: string): React.ReactNode => {
  // Try to detect JSON content
  const lines = content.split('\n');
  const formattedLines: React.ReactNode[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if line looks like JSON (starts with { or [ or contains JSON patterns)
    const jsonMatch = line.match(/^(\s*)([{[].*[}\]]|{.*}|[.*])(\s*)$/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[2]);
        const formatted = JSON.stringify(parsed, null, 2);
        formattedLines.push(
          <pre key={i} className="bg-muted/30 p-2 rounded text-xs font-mono overflow-x-auto">
            <code>{formatted}</code>
          </pre>
        );
        continue;
      } catch (e) {
        // Not valid JSON, treat as regular text
      }
    }
    
    // Check for inline JSON objects in text
    const inlineJsonRegex = /({[^{}]*}|[[^[\]]*])/g;
    if (inlineJsonRegex.test(line)) {
      const parts = line.split(inlineJsonRegex);
      const formattedParts = parts.map((part, partIndex) => {
        if (part.match(/^[{[].*[}\]]$/)) {
          try {
            const parsed = JSON.parse(part);
            const formatted = JSON.stringify(parsed, null, 2);
            return (
              <code key={partIndex} className="bg-muted/30 px-1 rounded text-xs font-mono">
                {formatted}
              </code>
            );
          } catch (e) {
            return part;
          }
        }
        return part;
      });
      formattedLines.push(<span key={i}>{formattedParts}</span>);
      if (i < lines.length - 1) formattedLines.push(<br key={`br-${i}`} />);
    } else {
      formattedLines.push(line);
      if (i < lines.length - 1) formattedLines.push(<br key={`br-${i}`} />);
    }
  }
  
  return <>{formattedLines}</>;
};

const RegularMessage = ({ message, onFeedback }: RegularMessageProps) => {

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = message.content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className={cn(
      "flex items-start mb-4",
      message.type === 'user' ? 'justify-end' : 'justify-start'
    )}>
      {message.type === 'bot' && (
        <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-xs font-medium mr-3 mt-0.5 flex-shrink-0 text-green-700 dark:text-green-400">
          AI
        </div>
      )}
      <div className={cn(
        "text-sm flex-1",
        message.type === 'user'
          ? 'bg-muted/50 text-foreground px-4 py-3 rounded-2xl rounded-tr-sm max-w-[80%] ml-12 text-right'
          : 'text-foreground'
      )}>
        {/* Display attached images for user messages */}
        {message.type === 'user' && message.attachments && message.attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {message.attachments.map((attachment, index) => (
              <img
                key={index}
                src={attachment.data_url}
                alt={`Attachment ${index + 1}`}
                className="max-w-48 max-h-32 object-contain rounded border"
              />
            ))}
          </div>
        )}
        <div className="whitespace-pre-wrap">{formatContent(message.content)}</div>
        {message.type === 'bot' && !message.isStep && !message.toolCall && message.content && (
          <div className="flex items-center space-x-1 mt-2">
              <button
                onClick={handleCopy}
                className="p-1 rounded transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                aria-label="Copy message"
              >
                <Copy size={12} />
              </button>
              <button
                onClick={() => onFeedback(message.id, 'thumbs_up')}
                className={cn(
                  "p-1 rounded transition-colors hover:bg-muted",
                  message.feedback === 'thumbs_up' 
                    ? "text-green-600" 
                    : "text-muted-foreground hover:text-foreground"
                )}
                aria-label="Thumbs up"
              >
                <ThumbsUp size={12} />
              </button>
              <button
                onClick={() => onFeedback(message.id, 'thumbs_down')}
                className={cn(
                  "p-1 rounded transition-colors hover:bg-muted",
                  message.feedback === 'thumbs_down' 
                    ? "text-red-600" 
                    : "text-muted-foreground hover:text-foreground"
                )}
                aria-label="Thumbs down"
              >
                <ThumbsDown size={12} />
              </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegularMessage;